import React from 'react';
import { TFunction } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import {
  view,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import {
  ModalView,
  Stack,
  Form,
  TextFieldControl,
  withTranslation,
} from '@piwikpro/ui-components';
import { MetaSitesState } from '@piwikpro/meta-sites-crate/reducers';
import { FormValidator } from '@piwikpro/form-crate';
import TitleComponent from '@piwikpro/title-crate/Title';
import { MetaSites } from '@piwikpro/meta-sites-crate';

const mapStateToProps = (state: MetaSitesState) => ({
  listFilters: state.metaSites.list.filters,
  metaSite: state.metaSites.details.settings.settings,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  metaSites: MetaSites
  validation: FormValidator
  Title: typeof TitleComponent
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

class Rename extends React.Component<Props> {
  constructor(props) {
    super(props);

    this.handleCancel = this.handleCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleCancel() {
    const { routerService } = this.props;

    routerService.dismissAdministrationOverlay();
  }

  async handleSubmit(values, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }) {
    const {
      routerService,
      metaSites,
      metaSite,
      listFilters,
    } = this.props;
    const name = values.name.trim();

    try {
      if (name.length > 0) {
        await metaSites.updateMetaSite(metaSite.id, { name });

        routerService.dismissAdministrationOverlay();

        await metaSites.fetchMetaSites(listFilters);
        await metaSites.fetchMetaSiteSettings(metaSite.id);
        await metaSites.fetchMetaSiteIntegrity(metaSite.id);
      } else {
        setSubmitting(false);
        setFieldValue('name', name, true);
      }
    } catch (err: any) {
      setErrors(err.errors);
      setSubmitting(false);
    }
  }

  render() {
    const {
      metaSite,
      validation,
      t,
      Title,
    } = this.props;

    return (
      <Title part={t('meta-sites.rename.title', { metaSiteName: metaSite.attributes.name })} subset>
        <Form
          id="rename-meta-site-form"
          initialValues={{
            name: metaSite.attributes.name,
          }}
          validationSchema={validation.createValidationSchema(validators => ({
            name: validators.metaSites.name,
          }))}
          onSubmit={this.handleSubmit}
        >
          <ModalView
            title={t('meta-sites.rename.title', { metaSiteName: metaSite.attributes.name })}
            actions={(
              <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
                <Form.Cancel onClick={this.handleCancel} />
                <Form.Submit />
              </Stack>
            )}
            onClose={this.handleCancel}
          >
            <TextFieldControl
              label={t('meta-sites.rename.fields.name')}
              name="name"
              required
              autoFocus
            />
          </ModalView>
        </Form>
      </Title>
    );
  }
}

export default connector(withInject<InjectedProps>({
  validation: 'FormCrate.validation',
  metaSites: 'MetaSitesCrate.metaSites',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(view({ role: 'USER', level: 'manage' })(withTranslation(['administration'])(
  errorBoundary(Rename),
))));
